import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Box } from '@mui/material';
import { Drawer, drawerType } from '../../../../../../packages/ui/organisms/drawer';
import {
  btnType,
  Button,
  Checkbox,
  Collapsable,
  fontWeight,
  FormControlLabel,
  Heading,
  headingLevel,
  IconButton,
  Input,
  inputSize,
  Text,
  TextError,
} from '../../../../../../packages';
import { UploadFile } from '../../../../../../packages/ui/templates/upload-file';
import UploadFileProgress from '../../../../../../pages/provider/profile/components/upload-flie-progress';
import { PayerSchema } from './AddPayerDrawer.schema';
import { useStyles } from './AddPayerDrawer.styles';
import { first } from 'lodash';

const AddPayerDrawer = ({ isOpen, selectedPayer, onSubmit, onClose }) => {
  const classes = useStyles();
  const [imageFile, setImageFile] = useState(null);
  const [imageFileURL, setImageFileURL] = useState('');
  const [imageFileError] = useState('');
  const [isImageUpdated, setIsImageUpdated] = useState(false);

  const [extraFile, setExtraFile] = useState(null);
  const [extraFileURL, setExtraFileURL] = useState('');
  const [extraFileError] = useState('');
  const [isExtraFileUpdated, setIsExtraFileUpdated] = useState(false);

  const defaultValues = {
    name: selectedPayer?.name || '',
    code: selectedPayer?.code || '',
    isCreditCardOptional: selectedPayer?.isCreditCardOptional || false,
    isActive: selectedPayer?.isActive || false,
    takeDeposit: selectedPayer?.takeDeposit || false,
    depositAmount: selectedPayer?.takeDeposit ? selectedPayer?.depositAmount : 0,
    candidPayerId: selectedPayer?.candidPayerId ?? '',
    candidPayerName: selectedPayer?.candidPayerName ?? '',
    parProofNotes: selectedPayer?.parProofNotes || '',
    parProofDate: selectedPayer?.parProofDate || '',
    parProofDocument: selectedPayer?.parProofDocument || '',

    credentialingPrimaryContact: {
      firstName: selectedPayer?.credentialingPrimaryContact?.firstName || '',
      lastName: selectedPayer?.credentialingPrimaryContact?.lastName || '',
      title: selectedPayer?.credentialingPrimaryContact?.title || '',
      phone: selectedPayer?.credentialingPrimaryContact?.phone || '',
      email: selectedPayer?.credentialingPrimaryContact?.email || '',
      fax: selectedPayer?.credentialingPrimaryContact?.fax || '',
      note: selectedPayer?.credentialingPrimaryContact?.note || '',
    },
    credentialingSecondaryContact: {
      firstName: selectedPayer?.credentialingSecondaryContact?.firstName || '',
      lastName: selectedPayer?.credentialingSecondaryContact?.lastName || '',
      title: selectedPayer?.credentialingSecondaryContact?.title || '',
      phone: selectedPayer?.credentialingSecondaryContact?.phone || '',
      email: selectedPayer?.credentialingSecondaryContact?.email || '',
      fax: selectedPayer?.credentialingSecondaryContact?.fax || '',
      note: selectedPayer?.credentialingSecondaryContact?.note || '',
    },
    billingPrimaryContact: {
      firstName: selectedPayer?.billingPrimaryContact?.firstName || '',
      lastName: selectedPayer?.billingPrimaryContact?.lastName || '',
      title: selectedPayer?.billingPrimaryContact?.title || '',
      phone: selectedPayer?.billingPrimaryContact?.phone || '',
      email: selectedPayer?.billingPrimaryContact?.email || '',
      fax: selectedPayer?.billingPrimaryContact?.fax || '',
      note: selectedPayer?.billingPrimaryContact?.note || '',
    },
    billingSecondaryContact: {
      firstName: selectedPayer?.billingSecondaryContact?.firstName || '',
      lastName: selectedPayer?.billingSecondaryContact?.lastName || '',
      title: selectedPayer?.billingSecondaryContact?.title || '',
      phone: selectedPayer?.billingSecondaryContact?.phone || '',
      email: selectedPayer?.billingSecondaryContact?.email || '',
      fax: selectedPayer?.billingSecondaryContact?.fax || '',
      note: selectedPayer?.billingSecondaryContact?.note || '',
    },
  };

  const { errors, values, handleChange, handleSubmit, touched, setFieldValue, ...rest } = useFormik({
    initialValues: defaultValues,
    enableReinitialize: true,
    validationSchema: PayerSchema,
    onSubmit: payload => {
      const finalPayload = {
        ...payload,
        depositAmount: selectedPayer?.takeDeposit ? payload?.depositAmount : 0,
        image: isImageUpdated ? imageFileURL : payload?.image,
        parProofDocument: isExtraFileUpdated ? extraFileURL : payload?.parProofDocument,
      };
      // Remove parProofDate if it's empty, null, or undefined
      if (!finalPayload.parProofDate || finalPayload.parProofDate === '') {
        delete finalPayload.parProofDate;
      }
      onSubmit(finalPayload, isImageUpdated);
    },
  });

  const setTouched = name => async () => {
    await rest.setTouched({ ...touched, [name]: true });
  };

  /**
   * @Name useEffect
   * @description This method is used when we get avatar URl from update flow
   */
  useEffect(() => {
    if (selectedPayer?.image) {
      setImageFile(selectedPayer.image);
    } else {
      setImageFile(null);
    }
  }, [selectedPayer]);

  /**
   * @Name onAcceptedFiles
   * @description This method is used when we select/accept any file
   */
  const onAcceptedFiles = files => {
    const file = files[0];
    setImageFile(file);
    setIsImageUpdated(true);
  };

  const onAcceptedExtraFile = files => {
    const file = files[0];
    setExtraFile(file);
    setIsExtraFileUpdated(true);
  };

  useEffect(() => {
    if (!isOpen) {
      rest.handleReset({ ...defaultValues });
      rest.setErrors({});
    }
  }, [isOpen]);

  const renderLabel = label => (
    <Heading level={headingLevel.S} className={classes.label} weight={fontWeight.BOLD}>
      {label}
    </Heading>
  );

  const renderContactFields = (prefix, contactValues, contactTouched, contactErrors) => (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', paddingTop: '8px' }}>
        {['firstName', 'lastName', 'title', 'phone', 'email', 'fax'].map(field => (
          <Box className={classes.section} key={field}>
            {renderLabel(
              (field === 'firstName' ? 'First Name' : field === 'lastName' ? 'Last Name' : field).replace(
                /^\w/,
                c => c.toUpperCase(),
              ),
            )}
            <Input
              fullWidth
              name={`${prefix}.${field}`}
              value={contactValues[field]}
              size={inputSize.M}
              onBlur={setTouched(`${prefix}.${field}`)}
              onChange={handleChange}
            />
            <TextError errorMsg={contactTouched?.[field] ? contactErrors?.[field] : null} />
          </Box>
        ))}
      </div>{' '}
      <div style={{ display: 'flex', padding: '24px 0 0 0', gap: '8px' }}>
        <Box
          className={classes.section}
          key={'note'}
          style={{ display: 'flex', width: '100%', height: '100px' }}
        >
          {renderLabel('note'.replace(/^\w/, c => c.toUpperCase()))}
          <Input
            fullWidth
            name={`${prefix}.note`}
            value={contactValues['note']}
            size={inputSize.M}
            onBlur={setTouched(`${prefix}.note`)}
            onChange={handleChange}
          />
          <TextError errorMsg={contactTouched?.['note'] ? contactErrors?.['note'] : null} />
        </Box>
      </div>
    </div>
  );

  return (
    <Drawer open={isOpen} onClose={onClose} variant={drawerType.NORMAL} className={classes.drawer}>
      <Box className={classes.wrapper}>
        <Box className={classes.header}>
          <Text className={classes.headerTitle}>{selectedPayer ? 'Edit' : 'Add'} Payer</Text>
          <IconButton icon="close" onClick={onClose} className={classes.backBtn} />
        </Box>

        <form className={classes.form} onSubmit={handleSubmit}>
          <Box className={classes.formContent}>
            {/* Existing fields */}
            <Box className={classes.section}>
              {renderLabel('Payer Name')}
              <Input
                fullWidth
                name="name"
                value={values.name}
                size={inputSize.M}
                onBlur={setTouched('name')}
                onChange={handleChange}
              />
              <TextError errorMsg={touched.name ? errors.name : null} />
            </Box>

            <Box className={classes.section}>
              {renderLabel('Payer Code')}
              <Input
                fullWidth
                name="code"
                value={values.code}
                size={inputSize.M}
                onBlur={setTouched('code')}
                onChange={handleChange}
              />
              <TextError errorMsg={touched.code ? errors.code : null} />
            </Box>
            <Box className={classes.section}>
              {renderLabel('Candid Payer Id')}
              <Input
                fullWidth
                name="candidPayerId"
                value={values.candidPayerId}
                placeholder=""
                size={inputSize.M}
                onBlur={setTouched('candidPayerId')}
                onChange={handleChange}
              />
              <TextError errorMsg={touched.candidPayerId ? errors.candidPayerId : null} />
            </Box>
            <Box className={classes.section}>
              {renderLabel('Candid Payer Name')}
              <Input
                fullWidth
                name="candidPayerName"
                value={values.candidPayerName}
                placeholder=""
                size={inputSize.M}
                onBlur={setTouched('candidPayerName')}
                onChange={handleChange}
              />
              <TextError errorMsg={touched.candidPayerName ? errors.candidPayerName : null} />
            </Box>
            <Box className={classes.section}>
              {renderLabel('Take Deposit')}
              <FormControlLabel
                control={<Checkbox />}
                checked={values?.takeDeposit}
                name="takeDeposit"
                onChange={() => {
                  void setFieldValue('takeDeposit', !values?.takeDeposit);
                }}
                label="Take Deposit"
              />
              <TextError errorMsg={touched.takeDeposit ? errors.takeDeposit : null} />
            </Box>
            {values?.takeDeposit && (
              <Box className={classes.section}>
                {renderLabel('Deposit Amount')}
                <Input
                  fullWidth
                  name="depositAmount"
                  value={values.depositAmount}
                  placeholder=""
                  size={inputSize.M}
                  onBlur={setTouched('depositAmount')}
                  onChange={handleChange}
                />
                <TextError errorMsg={touched.depositAmount ? errors.depositAmount : null} />
              </Box>
            )}
            <Box className={classes.section}>
              {renderLabel('Status')}
              <FormControlLabel
                control={<Checkbox />}
                checked={values.isActive}
                name="isActive"
                onChange={() => {
                  void setFieldValue('isActive', !values.isActive);
                }}
                label="Active"
              />
              <TextError errorMsg={touched.isActive ? errors.isActive : null} />
            </Box>
            <Box className={classes.section}>
              {renderLabel('Credit Card Details')}
              <FormControlLabel
                control={<Checkbox />}
                checked={values.isCreditCardOptional}
                name="isCreditCardOptional"
                onChange={() => {
                  void setFieldValue('isCreditCardOptional', !values.isCreditCardOptional);
                }}
                label="Is Medicaid"
              />
            </Box>
            <Box className={classes.section}>
              {renderLabel('Logo Image')}
              {imageFile ? (
                <UploadFileProgress
                  file={imageFile}
                  fileName={imageFile?.name || 'image.png'}
                  fileSize={imageFile?.size || 1024}
                  s3Folder="profileImages/"
                  onUploaded={url => {
                    setImageFileURL(url);
                  }}
                  onRemove={() => {
                    setImageFile(null);
                  }}
                />
              ) : (
                <UploadFile
                  onAcceptedFiles={onAcceptedFiles}
                  description="PNG, JPG, GIF up to 5MB"
                  fileType="image/*"
                />
              )}
              {!imageFile && <TextError errorMsg={imageFileError} />}
            </Box>
            {/* New top-level fields */}

            <Box className={classes.section}>
              {renderLabel('PAR Proof Date')}
              <Input
                fullWidth
                type="date"
                name="parProofDate"
                value={values.parProofDate}
                onBlur={setTouched('parProofDate')}
                onChange={handleChange}
              />
              <TextError errorMsg={touched.parProofDate ? errors.parProofDate : null} />
            </Box>

            <Box className={classes.section}>
              {renderLabel('PAR Proof')}
              {extraFile ? (
                <UploadFileProgress
                  file={extraFile}
                  fileName={extraFile?.name || 'parProofDocument.png'}
                  fileSize={extraFile?.size || 1024}
                  s3Folder="profileImages/"
                  onUploaded={url => setExtraFileURL(url)}
                  onRemove={() => setExtraFile(null)}
                />
              ) : (
                <UploadFile
                  onAcceptedFiles={onAcceptedExtraFile}
                  description="PNG, JPG, GIF up to 5MB"
                  fileType="image/*"
                />
              )}
              {!extraFile && <TextError errorMsg={extraFileError} />}
            </Box>
            <Box className={classes.section}>
              {renderLabel('PAR Note')}
              <Input
                fullWidth
                multiline
                rows={3}
                name="parProofNotes"
                value={values.parProofNotes}
                onBlur={setTouched('parProofNotes')}
                onChange={handleChange}
              />
              <TextError errorMsg={touched.parProofNotes ? errors.parProofNotes : null} />
            </Box>
            {/* Contacts Section */}
            <Box className={classes.section}>
              {renderLabel('Primary Credentialing Contact')}
              {renderContactFields(
                'credentialingPrimaryContact',
                values.credentialingPrimaryContact,
                touched.credentialingPrimaryContact,
                errors.credentialingPrimaryContact,
              )}
            </Box>
            <Box className={classes.section}>
              {renderLabel('Secondary Credentialing Contact')}
              {renderContactFields(
                'credentialingSecondaryContact',
                values.credentialingSecondaryContact,
                touched.credentialingSecondaryContact,
                errors.credentialingSecondaryContact,
              )}
            </Box>

            <Box className={classes.section}>
              {renderLabel('Primary Billing Contact')}
              {renderContactFields(
                'billingPrimaryContact',
                values.billingPrimaryContact,
                touched.billingPrimaryContact,
                errors.billingPrimaryContact,
              )}
            </Box>
            <Box className={classes.section}>
              {renderLabel('Secondary Billing Contact')}
              {renderContactFields(
                'billingSecondaryContact',
                values.billingSecondaryContact,
                touched.billingSecondaryContact,
                errors.billingSecondaryContact,
              )}
            </Box>
          </Box>

          <Box className={classes.footer}>
            <Button variant={btnType.TEXT} onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit}>Save</Button>
          </Box>
        </form>
      </Box>
    </Drawer>
  );
};

export { AddPayerDrawer };
